import React, { useCallback, useRef } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.core.css';
import { InfoHint } from '../hint/InfoHint';
import { FormGroup } from '../form/FormGroup';
import InputError from '../form/InputError';
import InputLabel from '../form/InputLabel';
import ScrollToError from './ScrollToError';
import { getError } from './utils';

type Props = {
  name: string;
  required?: boolean;
  disabled?: boolean;
  label?: React.ReactNode;
  hint?: React.ReactNode;
  onChange?: (value: string) => void;
};

const modules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    ['blockquote', 'code-block'],
    [
      { list: 'ordered' },
      { list: 'bullet' },
      { indent: '-1' },
      { indent: '+1' },
    ],
    ['link'],
    ['clean'],
  ],
};

const EMPTY_CONTENT = '<p><br></p>';

export const RichtextInputStyled: React.FC<
  React.PropsWithChildren<ReactQuill.ReactQuillProps>
> = (props) => {
  return <ReactQuill {...props} modules={modules} />;
};

export const RichtextInput: React.FC<React.PropsWithChildren<Props>> = ({
  name,
  label,
  required,
  disabled,
  hint,
  onChange,
}) => {
  const ref = useRef(null);
  const {
    control,
    formState: { errors, isSubmitting },
  } = useFormContext();

  const error = getError(name, errors);

  const normalizeValue = useCallback(
    (value: string) => {
      const normalizedValue = value === EMPTY_CONTENT ? '' : value;
      onChange?.(normalizedValue);
      return normalizedValue;
    },
    [onChange],
  );

  return (
    <FormGroup ref={ref}>
      {label && (
        <InputLabel
          label={label}
          required={required}
          hint={
            hint ? <InfoHint className="mt-2" text={String(hint)} /> : undefined
          }
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }) => (
          <RichtextInputStyled
            value={field.value ?? ''}
            onBlur={field.onBlur}
            onChange={(value) => field.onChange(normalizeValue(value))}
            className={error ? 'is-invalid' : ''}
            readOnly={isSubmitting || disabled}
          />
        )}
      />
      <InputError error={error} />
      <ScrollToError name={name} inputContainerRef={ref} />
    </FormGroup>
  );
};
